import React from "react";
import { Spin } from "antd";

import { useGetUserWebAiChatsQuery } from "graphql/queries/get-user-web-ai-chats.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";

import { AiChatType } from "constants/field-values";

// Maximum length of the title shown in the list. Longer titles are cut and end with an ellipsis.
const TITLE_MAX_LENGTH = 44;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Returns the date label for a conversation: "Today" or "Yesterday" for the last two days, and the
// full date for anything older.
const getDayLabel = (createdAt: string): string => {
	const startOfCreated = new Date(createdAt);

	if (Number.isNaN(startOfCreated.getTime())) return "";

	startOfCreated.setHours(0, 0, 0, 0);

	const startOfToday = new Date();
	startOfToday.setHours(0, 0, 0, 0);

	const daysAgo = Math.round((startOfToday.getTime() - startOfCreated.getTime()) / MS_PER_DAY);

	if (daysAgo <= 0) return "Today";
	if (daysAgo === 1) return "Yesterday";

	return new Date(createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

interface OllieChatHistoryProps {
	onOpenThread: (doctrineChatId: string) => void;
}

// List of the user's previous Ollie conversations. This component is only mounted while the history
// is open, so the query runs when the user asks for the list and not on every /ollie screen.
const OllieChatHistory: React.FC<OllieChatHistoryProps> = ({ onOpenThread }) => {
	const { loggedUserValue } = useLoggedUser();

	const { data, loading } = useGetUserWebAiChatsQuery({
		variables: { loggedUserId: loggedUserValue.id, chat_type: AiChatType.OLLIE },
		skip: !loggedUserValue.id,
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	const { ai_chats = [] } = data || {};

	return (
		<div className="och-list">
			<span className="och-title">Previous conversations</span>

			{loading && !ai_chats.length ? (
				<div className="och-loading">
					<Spin />
				</div>
			) : null}

			{!loading && !ai_chats.length ? (
				<span className="och-empty">Nothing here yet. Ask Ollie something and it will show up.</span>
			) : null}

			{ai_chats.map(chat => {
				const { id, doctrine_chat_id, doctrine_title, starter_message, created_at } = chat || {};
				const title = doctrine_title || starter_message || "Untitled conversation";

				return (
					<button
						type="button"
						key={`och-item-${id}`}
						className="och-item"
						onClick={() => onOpenThread(doctrine_chat_id)}
					>
						<span className="och-item-title">
							{title.length > TITLE_MAX_LENGTH ? `${title.slice(0, TITLE_MAX_LENGTH).trimEnd()}…` : title}
						</span>
						<span className="och-item-date">{getDayLabel(created_at)}</span>
					</button>
				);
			})}
		</div>
	);
};

export default OllieChatHistory;
